import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, X, Plus } from "lucide-react";
import { toast } from "sonner";

const ADD = "__add_industry__";

export function useIndustries() {
  const [industries, setIndustries] = useState([]);
  const load = () => api.get("/industries").then((r) => setIndustries(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return { industries, reload: load };
}

export default function IndustrySelect({ value, onChange, className = "", testid = "industry-select" }) {
  const { industries, reload } = useIndustries();
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!label.trim()) { toast.error("Enter an industry name."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/industries", { label: label.trim() });
      await reload();
      onChange(data.value);
      setAdding(false); setLabel("");
      toast.success("Industry added");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (adding) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Input autoFocus value={label} placeholder="New industry" onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`${testid}-new-input`} />
        <Button type="button" size="icon" className="h-9 w-9 shrink-0" onClick={add} disabled={saving} data-testid={`${testid}-new-save`}><Check className="h-4 w-4" /></Button>
        <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => { setAdding(false); setLabel(""); }}><X className="h-4 w-4" /></Button>
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={(v) => (v === ADD ? setAdding(true) : onChange(v))}>
      <SelectTrigger className={className} data-testid={testid}><SelectValue placeholder="Select Industry" /></SelectTrigger>
      <SelectContent>
        {industries.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
        <SelectItem value={ADD} className="text-primary" data-testid={`${testid}-add-option`}>
          <span className="flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add new industry…</span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
